import { useState } from 'react'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { useEditConcreteTestSetMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import CIMSFormItem from 'components/CIMSFormItem'
import { CIMSModal } from 'components/CIMSModal'
import { useConcreteTestSetTrackerContext } from '../../ConcreteTestSetTrackerContext'
import CoreFormItems from './components/CoreFormItems'
import TestFormItems from './components/TestFormItems'
import {
  BreakDataFormItems,
  BreakDataFormItemsValue,
  BreakDataModalProps,
} from './types'
import { CoreFormItemsValue } from './components/CoreFormItems/types'
import { toMoment } from 'utils/toMoment'
import { useLabTechSelectContext } from 'components/CIMSSelect/common/LabTechSelect/LabTechSelectContext'
import { TestFormItemsValue } from './components/TestFormItems/types'
import { useBreakDateByPlacementDate } from '../../hooks/useBreakDateByPlacementDate'
import { useTestingLabSelectContext } from 'components/CIMSSelect/common/TestingLabSelect/TestingLabSelectContext'
import { Space } from 'antd'
import CIMSBasicFields from 'components/CIMSBasicFields'
import { HelpMessage } from 'utils/constants'
import CIMSSection from 'components/CIMSSection'
import { errorHandler } from 'hooks/errorHandler'
import { dateFormatter } from '../../../../../../../../../utils/dateFormatter'

const BreakDataModal: React.FC<BreakDataModalProps> = ({
  onClose,
  visible,
  state,
}) => {
  const {
    globalId,
    testSetId,
    concretePlacements = [],
    concreteTests = [],
    placementDate,
  } = state ?? {}
  const { relations } = useConcreteTestSetTrackerContext()
  const [edit] = useEditConcreteTestSetMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editConcreteTestSet: {
        concreteTestSet: { testSetId },
      },
    }) =>
      simpleSuccessNotification(
        `Break Data for ${testSetId} successfully edited!`
      ),
  })
  const [editing, setEditing] = useState(false)
  const labTechSelectContext = useLabTechSelectContext()
  const testingLabSelectContext = useTestingLabSelectContext()
  const breakDateByPlacementDate = useBreakDateByPlacementDate(placementDate)
  const initialFormValues: BreakDataFormItems = {
    cores: concretePlacements.flatMap(({ concreteCoreMeasurements }) =>
      concreteCoreMeasurements.map<CoreFormItemsValue>(
        ({ globalId, concreteCoreBreakData }) => {
          if (!concreteCoreBreakData)
            return {
              age: 28,
              concreteCoreMeasurementId: globalId,
              breakDate: breakDateByPlacementDate()!,
            }
          const { labTech, testingLab, breakDate, age, __typename, ...rest } =
            concreteCoreBreakData

          return {
            ...rest,
            concreteCoreMeasurementId: globalId,
            age: age ?? 28,
            breakDate: toMoment(breakDate),
            labTech: labTechSelectContext.toLabeledValue(labTech),
            testingLab: testingLabSelectContext.toLabeledValue(testingLab),
          }
        }
      )
    ),
    tests: concreteTests.reduce((values, concreteTest) => {
      const { concreteTestBreakData, strengthAges, globalId } = concreteTest
      if (!concreteTestBreakData.length)
        values.push(
          ...strengthAges.map<TestFormItemsValue>((age) => ({
            tempId: crypto.randomUUID(),
            concreteTestId: globalId,
            age,
            breakDate: breakDateByPlacementDate(age),
          }))
        )
      else
        values.push(
          ...concreteTestBreakData.map<TestFormItemsValue>(
            ({ age, breakDate, labTech, testingLab, __typename, ...rest }) => ({
              ...rest,
              concreteTestId: globalId,
              age: age ?? 28,
              breakDate: toMoment(breakDate),
              labTech: labTechSelectContext.toLabeledValue(labTech),
              testingLab: testingLabSelectContext.toLabeledValue(testingLab),
            })
          )
        )
      return values
    }, Array<TestFormItemsValue>()),
  }
  const lab = useTestingLabSelectContext()
  const tech = useLabTechSelectContext()
  return (
    <CIMSModal
      visible={visible}
      onCancel={onClose}
      title={`Edit ${testSetId} Break Data`}
      loading={relations.loading || editing}
      disabled={relations.loading || editing}
      width="wide"
      height="tall"
      initialFormValues={initialFormValues}
      onOk={async ({ cores, tests }) => {
        if (!globalId) return errorHandler('Global id must be defined')

        function mapper<T extends BreakDataFormItemsValue>(
          { breakDate, labTech, testingLab, ...rest }: T,
          ..._ignored: any[]
        ) {
          return {
            ...rest,
            breakDate: breakDate?.toISOString(),
            labTechId: tech.fromLabeledValue(labTech),
            testingLabId: lab.fromLabeledValue(testingLab),
          }
        }
        const concreteCoreBreakData = cores?.map(mapper)
        const concreteTestBreakData = tests?.map(
          ({ tempId: _ignored, ...rest }) => mapper(rest)
        )
        setEditing(true)
        await edit({
          variables: {
            /**
             * TS correctly complains of required properties from
             * {@link concreteCoreBreakData} items and {@link concreteTestBreakData} items that could be
             * `undefined` or `null`, but we currently rely on server side
             * validation for these properties instead of client validation.
             */
            // @ts-expect-error
            input: { globalId, concreteCoreBreakData, concreteTestBreakData },
          },
        })
        setEditing(false)
        relations.refetch()
        onClose()
      }}
    >
      {placementDate && (
        <Space style={{ width: '100%' }} size="small" direction="vertical">
          <CIMSSection title="Concrete test set">
            <CIMSBasicFields
              fields={[
                {
                  type: 'date',
                  date: placementDate,
                  help: HelpMessage.Concrete.ConcreteTestSetTracker.Date,
                  label: 'Placement Date',
                  copyable: dateFormatter.custom(placementDate, 'YYYY-MM-DD'),
                },
              ]}
            />
          </CIMSSection>
        </Space>
      )}
      {!!initialFormValues.cores?.length && (
        <CIMSFormItem name="cores">
          <CoreFormItems concretePlacements={concretePlacements} />
        </CIMSFormItem>
      )}
      <CIMSFormItem name="tests">
        <TestFormItems
          concreteTests={concreteTests}
          placementDate={placementDate ?? null}
        />
      </CIMSFormItem>
    </CIMSModal>
  )
}

export default BreakDataModal
